/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { MAX_ESS_COUNT, MAX_THERMAL_COUNT } from '../models/plant-sim';
import { fromObjectEntries, objectEntries } from '../utils';
const range1 = (count) => Array.from({ length: count }, (_, i) => i + 1);
export const MAX_NET_BENEFIT_PV_COUNT = 10;
export const MAX_NET_BENEFIT_AS_AVAILABLE_COUNT = 10;
/**
 * Per-asset report columns are keyed by asset label (`PV2 Gen [MWh] 2029`,
 * `BESS power [MW]`). The reader matches headers against these suffixes
 * (longest first), classifies each label's asset type via the single-type
 * patterns, and assigns indexes by first appearance per type.
 */
const assetSuffixPatterns = [
    {
        suffix: ' MV Bus Clipping [MWh]',
        metric: 'mvbus_clipping_mwh',
        kind: 'byYear',
        types: ['pv', 'as_available'],
    },
    { suffix: ' AC Inverters [MWac]', metric: 'ac_inverters', kind: 'static', types: ['pv'] },
    { suffix: ' Capacity Cost [$]', metric: 'capacity_cost', kind: 'byYear', types: ['ess'] },
    { suffix: ' Discharge [MWh]', metric: 'discharge', kind: 'byYear', types: ['ess'] },
    { suffix: ' AC POI [MWac]', metric: 'ac_poi', kind: 'static', types: ['pv'] },
    {
        suffix: ' O&M Cost [$]',
        metric: 'om_cost',
        kind: 'byYear',
        types: ['pv', 'as_available', 'ess'],
    },
    { suffix: ' duration [hr]', metric: 'duration_hr', kind: 'static', types: ['ess'] },
    { suffix: ' Capacity [MW]', metric: 'capacity_mw', kind: 'static', types: ['thermal'] },
    { suffix: ' Fuel Cost [$]', metric: 'fuel_cost', kind: 'byYear', types: ['thermal'] },
    { suffix: ' Toll Cost [$]', metric: 'toll_cost', kind: 'byYear', types: ['thermal'] },
    { suffix: ' FOM Cost [$]', metric: 'fom_cost', kind: 'byYear', types: ['thermal'] },
    { suffix: ' VOM Cost [$]', metric: 'vom_cost', kind: 'byYear', types: ['thermal'] },
    { suffix: ' energy [MWh]', metric: 'energy', kind: 'static', types: ['ess'] },
    { suffix: ' Charge [MWh]', metric: 'charge', kind: 'byYear', types: ['ess'] },
    { suffix: ' PPA Cost [$]', metric: 'ppa_cost', kind: 'byYear', types: ['pv', 'as_available'] },
    { suffix: ' power [MW]', metric: 'power_mw', kind: 'static', types: ['ess'] },
    {
        suffix: ' Gen [MWh]',
        metric: 'gen_mwh',
        kind: 'byYear',
        types: ['pv', 'as_available', 'thermal'],
    },
    {
        suffix: ' Capex [$]',
        metric: 'capex',
        kind: 'static',
        types: ['pv', 'as_available', 'ess', 'thermal'],
    },
    { suffix: ' DC [MWdc]', metric: 'dc_mwdc', kind: 'static', types: ['pv'] },
    { suffix: ' [MWac]', metric: 'mwac', kind: 'static', types: ['as_available'] },
];
export const netBenefitV2AssetSuffixPatterns = [...assetSuffixPatterns].sort((a, b) => b.suffix.length - a.suffix.length);
/** Wire key for a per-asset column, matching the template-literal column types. */
export const getNetBenefitV2AssetColumnKey = (type, index, metric) => {
    if (type === 'ess') {
        if (metric === 'charge' || metric === 'discharge')
            return `${metric}_${index}`;
        return `ess_${index}_${metric}`;
    }
    return `${type}_${index}_${metric}`;
};
export const netBenefitV2AssetLabelsKeys = {
    pv: 'pv_labels',
    as_available: 'as_available_labels',
    ess: 'ess_labels',
    thermal: 'thermal_labels',
};
export const netBenefitV2ColumnsLabelMap = {
    scenario: { label: 'Scenario #', customUnit: 'index' },
    ic_size_mw: { label: 'IC size [MW]' },
    pv_labels: { label: '__pv_labels__' },
    as_available_labels: { label: '__as_available_labels__' },
    ess_labels: { label: '__ess_labels__' },
    thermal_labels: { label: '__thermal_labels__' },
    ...fromObjectEntries(range1(MAX_NET_BENEFIT_PV_COUNT).flatMap((i) => [
        [
            `pv_${i}_ac_inverters`,
            { label: `PV ${i} AC Inverters [MWac]` },
        ],
        [`pv_${i}_ac_poi`, { label: `PV ${i} AC POI [MWac]` }],
        [`pv_${i}_dc_mwdc`, { label: `PV ${i} DC [MWdc]` }],
        [
            `pv_${i}_capex`,
            { label: `PV ${i} Capex [$]`, customUnit: '$' },
        ],
        [`pv_${i}_gen_mwh`, { label: `PV ${i} Gen [MWh]` }],
        [
            `pv_${i}_mvbus_clipping_mwh`,
            { label: `PV ${i} MV Bus Clipping [MWh]` },
        ],
        [
            `pv_${i}_ppa_cost`,
            { label: `PV ${i} PPA Cost [$]`, customUnit: '$' },
        ],
        [
            `pv_${i}_om_cost`,
            { label: `PV ${i} O&M Cost [$]`, customUnit: '$' },
        ],
    ])),
    ...fromObjectEntries(range1(MAX_NET_BENEFIT_AS_AVAILABLE_COUNT).flatMap((i) => [
        [
            `as_available_${i}_mwac`,
            { label: `As Available ${i} [MWac]` },
        ],
        [
            `as_available_${i}_capex`,
            { label: `As Available ${i} Capex [$]`, customUnit: '$' },
        ],
        [
            `as_available_${i}_gen_mwh`,
            { label: `As Available ${i} Gen [MWh]` },
        ],
        [
            `as_available_${i}_mvbus_clipping_mwh`,
            { label: `As Available ${i} MV Bus Clipping [MWh]` },
        ],
        [
            `as_available_${i}_ppa_cost`,
            { label: `As Available ${i} PPA Cost [$]`, customUnit: '$' },
        ],
        [
            `as_available_${i}_om_cost`,
            { label: `As Available ${i} O&M Cost [$]`, customUnit: '$' },
        ],
    ])),
    ...fromObjectEntries(range1(MAX_ESS_COUNT).flatMap((i) => [
        [`ess_${i}_power_mw`, { label: `ESS ${i} power [MW]` }],
        [`ess_${i}_duration_hr`, { label: `ESS ${i} duration [hr]` }],
        [`ess_${i}_energy`, { label: `ESS ${i} energy [MWh]` }],
        [`ess_${i}_capex`, { label: `ESS ${i} Capex [$]`, customUnit: '$' }],
        [`charge_${i}`, { label: `Charge ${i} [MWh]` }],
        [`discharge_${i}`, { label: `Discharge ${i} [MWh]` }],
        [
            `ess_${i}_capacity_cost`,
            { label: `ESS ${i} Capacity Cost [$]`, customUnit: '$' },
        ],
        [
            `ess_${i}_om_cost`,
            { label: `ESS ${i} O&M Cost [$]`, customUnit: '$' },
        ],
    ])),
    ...fromObjectEntries(range1(MAX_THERMAL_COUNT).flatMap((i) => [
        [
            `thermal_${i}_capacity_mw`,
            { label: `Thermal ${i} Capacity [MW]` },
        ],
        [
            `thermal_${i}_capex`,
            { label: `Thermal ${i} Capex [$]`, customUnit: '$' },
        ],
        [`thermal_${i}_gen_mwh`, { label: `Thermal ${i} Gen [MWh]` }],
        [
            `thermal_${i}_toll_cost`,
            { label: `Thermal ${i} Toll Cost [$]`, customUnit: '$' },
        ],
        [
            `thermal_${i}_fom_cost`,
            { label: `Thermal ${i} FOM Cost [$]`, customUnit: '$' },
        ],
        [
            `thermal_${i}_vom_cost`,
            { label: `Thermal ${i} VOM Cost [$]`, customUnit: '$' },
        ],
        [
            `thermal_${i}_fuel_cost`,
            { label: `Thermal ${i} Fuel Cost [$]`, customUnit: '$' },
        ],
    ])),
    npv_net_benefit: { label: 'NPV Net Benefit [$]', customUnit: '$' },
    npv_cost_normalized_net_benefit: {
        label: 'NPV Cost Normalized Net Benefit [$]',
        customUnit: '$',
    },
    npv_rec_revenue: { label: 'NPV REC Revenue [$]', customUnit: '$' },
    npv_capacity_revenue: { label: 'NPV Capacity Revenue [$]', customUnit: '$' },
    npv_value_of_delivery: { label: 'NPV Value of Delivery/Shape [$]', customUnit: '$' },
    npv_excess_revenue: { label: 'NPV Excess Revenue [$]', customUnit: '$' },
    npv_market_net_revenue: { label: 'NPV Market Net Revenue [$]', customUnit: '$' },
    npv_deficit_cost: { label: 'NPV Deficit Cost [$]', customUnit: '$' },
    npv_ownership_cost: { label: 'NPV Ownership Cost [$]', customUnit: '$' },
    npv_poi_cost: { label: 'NPV POI Cost [$]', customUnit: '$' },
    npv_pv_cost: { label: 'NPV PV Cost [$]', customUnit: '$' },
    npv_bess_cost: { label: 'NPV BESS Cost [$]', customUnit: '$' },
    npv_as_available_cost: { label: 'NPV As Available Cost [$]', customUnit: '$' },
    npv_thermal_cost: { label: 'NPV Thermal Cost [$]', customUnit: '$' },
    irr_perc: { label: 'IRR [%]', customUnit: '%' },
    ppa_price: { label: 'Project PPA [$/MWh] or Capacity [$/MW/mo] Price', customUnit: '$' },
    ppa_price_yearly: {
        label: 'Project PPA [$/MWh] or Capacity [$/MW/mo] Price Year',
        customUnit: '$',
    },
    shape_adjusted_project_ppa_price: {
        label: 'Shape Adjusted Project PPA Price [$/MWh]',
        customUnit: '$',
    },
    shape_adjusted_project_ppa_price_yearly: {
        label: 'Shape Adjusted Project PPA Price [$/MWh] Year',
        customUnit: '$',
    },
    load_ppa_price: { label: 'Load PPA Price [$/MWh]', customUnit: '$' },
    load_ppa_price_yearly: { label: 'Load PPA Price [$/MWh] Year', customUnit: '$' },
    load_ppa_price_risk_spread: { label: 'Load PPA Price Risk Spread [%]', customUnit: '%' },
    capex_after_itc: { label: 'Capex (after ITC) [$]', customUnit: '$' },
    npv_total_revenue: { label: 'NPV Total Revenue [$]', customUnit: '$' },
    npv_total_cost: { label: 'NPV Total Cost [$]', customUnit: '$' },
    itc_revenue: { label: 'ITC Revenue [$]', customUnit: '$' },
    poi_capex: { label: 'Other TXM/IC Capex [$]', customUnit: '$' },
    delivery_mwh: { label: 'Delivery [MWh]' },
    poi_clipping_mwh: { label: 'POI Clipping [MWh]' },
    poi_clipping_perc: { label: 'POI Clipping [%]', customUnit: '%' },
    total_shape: { label: 'Total Shape [MWh]' },
    peak_shape: { label: 'Peak Shape [MW]', customUnit: 'MW' },
    annual_shape_match: { label: 'Annual Shape Match [%]', customUnit: '%' },
    annual_renewable_shape_match: { label: 'Annual Renewable Shape Match [%]', customUnit: '%' },
    shape_met: { label: 'Shape Met [%]', customUnit: '%' },
    full_shape_met_frequency: { label: 'Full Shape Met Frequency [%]', customUnit: '%' },
    deficit: { label: 'Deficit [%]', customUnit: '%' },
    excess: { label: 'Excess [%]', customUnit: '%' },
    net_benefit: { label: 'Net Benefit [$]', customUnit: '$' },
    cost_normalized_net_benefit: { label: 'Cost Normalized Net Benefit [$]', customUnit: '$' },
    total_revenue: { label: 'Total Revenue [$]', customUnit: '$' },
    total_cost: { label: 'Total Cost [$]', customUnit: '$' },
    market_net_revenue: { label: 'Market Net Revenue [$]', customUnit: '$' },
    value_of_delivery: { label: 'Value of Delivery/Shape [$]', customUnit: '$' },
    excess_revenue: { label: 'Excess Revenue [$]', customUnit: '$' },
    deficit_cost: { label: 'Deficit Cost [$]', customUnit: '$' },
    rec_revenue: { label: 'REC Revenue [$]', customUnit: '$' },
    capacity_revenue: { label: 'Capacity Revenue [$]', customUnit: '$' },
    ptc_revenue: { label: 'PTC Revenue [$]', customUnit: '$' },
    poi_costs: { label: 'Other TXM/IC Costs [$]', customUnit: '$' },
    firm_gas_transport_cost: { label: 'Firm Gas Transport Cost [$]', customUnit: '$' },
    carbon_cost: { label: 'Carbon Cost [$]', customUnit: '$' },
    carbon_emissions: { label: 'Carbon Emissions [Tons]' },
    project_lcoe_benchmark: { label: 'Project LCOE Benchmark [$/MWh]', customUnit: '$' },
    project_lcoe_premium_over_benchmark: {
        label: 'Project LCOE Premium Over Benchmark [$/MWh]',
        customUnit: '$',
    },
    load_lcoe_benchmark: { label: 'Load LCOE Benchmark [$/MWh]', customUnit: '$' },
    load_lcoe_premium_over_benchmark: {
        label: 'Load LCOE Premium Over Benchmark [$/MWh]',
        customUnit: '$',
    },
};
export const netBenefitPlantSimDataV2ColumnsLabelMap = {
    delivery_mwh_yearly: { label: 'Delivery [MWh]' },
    poi_clipping_mwh_yearly: { label: 'POI Clipping [MWh]' },
    poi_clipping_perc_yearly: { label: 'POI Clipping [%]', customUnit: '%' },
    peak_shape_yearly: { label: 'Peak Shape [MW]', customUnit: 'MW' },
    total_shape_yearly: { label: 'Total Shape [MWh]' },
    deficit_yearly: { label: 'Deficit [%]', customUnit: '%' },
    excess_yearly: { label: 'Excess [%]', customUnit: '%' },
    shape_met_yearly: { label: 'Shape Met [%]', customUnit: '%' },
    full_shape_met_frequency_yearly: { label: 'Full Shape Met Frequency [%]', customUnit: '%' },
    annual_shape_match_yearly: { label: 'Annual Shape Match [%]', customUnit: '%' },
    annual_renewable_shape_match_yearly: {
        label: 'Annual Renewable Shape Match [%]',
        customUnit: '%',
    },
};
export const netBenefitV2ColumnsLabelToKeyMap = fromObjectEntries(objectEntries(netBenefitV2ColumnsLabelMap).map(([key, data]) => [data.label, key]));
export const netBenefitPlantSimDataV2ColumnsLabelToKeyMap = fromObjectEntries(objectEntries(netBenefitPlantSimDataV2ColumnsLabelMap).map(([key, data]) => [data.label, key]));
//# sourceMappingURL=net-benefit-results.js.map